import {
  Body,
  FormProvider,
  Input,
  Stack,
  useForm,
} from '@qred/components-library';
import { getCurrency } from '@qred/shared-component-library/src/currency';
import React, { useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ValidationContext } from '~/components/hoc/withValidation';
import { FormErrorBanner } from '~/components/shared/FormErrorBanner/FormErrorBanner';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import StepHeader from '~/components/shared/Stepper/components/StepHeader';
import TrustpilotWidget from '~/components/shared/TrustpilotWidget/TrustpilotWidget';
import { TurnoverSelect } from '~/components/shared/TurnoverSelect/TurnoverSelect';
import { marketHasTrustpilot } from '~/constants/markets';
import { purposeOfLoanIsValid } from '~/helpers/utils';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store';
import { updateForm } from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  StandardStepContent,
  StandardStepContentBody,
  StandardStepContentFooter,
} from '~/styles/BaseStyledComponents';
import { ForeignCurrencyIncome } from '../StartApplication/components/ForeignCurrencyIncome';
import { LoanPurposeSelect } from '../StartApplication/components/LoanPurposeSelect';
import { LastBasicStepSummaryBox } from '../Steps/LastBasicStepSummaryBox';
import useGoToNextStep from '../Steps/useGoToNextStep';
import useStepConfig from '../Steps/useStepConfig';
import { BusinessNeedsForm, BusinessNeedsSchema } from './BusinessNeeds.schema';

/**
 * Step C (and last basic step) of the 3-step basic flow (behind the
 * AB_Web_LoanApplication_ContactDetailsEarlier_MoreRecoverableLeads_NL flag,
 * currently tested in NL only): loan purpose, turnover, and foreign-income
 * question, previously collected together with the company on step 1.
 */
export const BusinessNeeds = () => {
  const {
    form: {
      purposeOfLoan,
      reason,
      turnover,
      incomeReceivedInForeignCurrencies,
    },
    apiStatus: applicationApiStatus,
  } = useSelector((state: RootState) => state.loanApplication);
  const { market } = useSelector((state: RootState) => state.intl);

  const t = useTranslate();
  const validationContext = useContext(ValidationContext);
  const dispatch = useAppDispatch();
  const { isLastStep, isLastBasicStep } = useStepConfig();
  const onNextStepClick = useGoToNextStep();

  const formMethods = useForm<BusinessNeedsForm>({
    schema: BusinessNeedsSchema,
    mode: 'onTouched',
    defaultValues: {
      loanPurpose: {
        purposeOfLoan: purposeOfLoanIsValid(purposeOfLoan)
          ? purposeOfLoan
          : undefined,
        reason,
      },
      turnover,
      incomeReceivedInForeignCurrencies,
    },
  });

  const {
    watch,
    formState: { errors, isValid },
  } = formMethods;

  const loanPurpose = watch('loanPurpose');
  const turnoverValue = watch('turnover');
  const incomeReceivedInForeignCurrenciesValue = watch(
    'incomeReceivedInForeignCurrencies'
  );

  useEffect(() => {
    if (!isValid) {
      validationContext.manuallyInvalidateForm(true);
    }

    return () => {
      validationContext.manuallyInvalidateForm(false);
    };
  }, [isValid]);

  useEffect(() => {
    if (isValid) {
      dispatch(
        updateForm({
          purposeOfLoan: loanPurpose.purposeOfLoan,
          reason: loanPurpose.reason,
          turnover: turnoverValue,
          incomeReceivedInForeignCurrencies: incomeReceivedInForeignCurrenciesValue,
        })
      );
    }
  }, [
    isValid,
    loanPurpose.purposeOfLoan,
    loanPurpose.reason,
    turnoverValue,
    incomeReceivedInForeignCurrenciesValue,
    dispatch,
  ]);

  const nextButtonLabel =
    isLastStep || isLastBasicStep ? 'Onboarding.Submit' : 'Onboarding.Next';

  return (
    <StandardStepContent>
      <StandardStepContentBody>
        <StepHeader
          title="Onboarding.BusinessNeedsTitle"
          description="Onboarding.BusinessNeedsDescription"
        />
        <FormProvider {...formMethods}>
          <Input.Group orientation="vertical">
            <LoanPurposeSelect />
            <TurnoverSelect
              label={`${t('Onboarding.TurnoverLabel') as string} (${
                getCurrency(market).unit
              })`}
              hintMessage={t('Onboarding.TurnoverHintMessage') as string}
            />
          </Input.Group>
          <ForeignCurrencyIncome />
        </FormProvider>
        <FormErrorBanner errors={errors} />
        {isValid && isLastBasicStep && <LastBasicStepSummaryBox />}
      </StandardStepContentBody>
      <StandardStepContentFooter>
        <NextButton
          label={nextButtonLabel}
          isLoading={applicationApiStatus.patch === ApiStatus.Started}
          action={onNextStepClick}
        />
        {isLastBasicStep && (
          <Stack spacing={'sm'}>
            {marketHasTrustpilot[market] && (
              <TrustpilotWidget templateId="5419b637fa0340045cd0c936" />
            )}
            <Body size="sm" display="block">
              {t('Onboarding.ApplicationProcessingConsent')}
            </Body>
          </Stack>
        )}
      </StandardStepContentFooter>
    </StandardStepContent>
  );
};
